import { maximumGridStyle } from '@/presentation/styles/global';
import {
  CaptionHeavy,
  CaptionLight,
  CashbackTag,
  Colors,
  DealActionStampButton,
  DealImage,
  DealImageSkeleton,
  DealStamp,
  DealVoteButton,
  Display,
  H1Heavy,
  H2Heavy,
  H3Heavy,
  MediaQuery,
  SmallHeavy,
  TextSkeleton,
  Theme,
  Timestamp,
} from '@pelando/components';
import { styled, css } from 'styled-components';

export const Container = styled.div`
  margin-top: 0;

  @media ${MediaQuery.SCREEN_MD_UP} {
    ${maximumGridStyle}
    margin-top: 12px;
  }
`;

export const DealInformationSectionContent = styled.div`
  background-color: rgb(${({ theme }) => theme.colors.Juliet});
  height: 100%;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border: 1px solid rgb(${({ theme }) => theme.colors.Golf});
    border-radius: 16px;
    padding: 40px;
    gap: 32px;
  }
`;

export const InformationContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 512px;
  position: relative;
`;

export const Image = styled(DealImage)`
  && {
    width: 355px;
    grid-area: image;
    cursor: pointer;

    @media ${MediaQuery.SCREEN_MD_UP} {
      height: 240px;
    }
  }
`;

export const ActionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  width: 100%;
  padding: 8px 16px 16px;
  justify-content: space-between;
  grid-template-areas:
    'actions'
    'title'
    'priceAndOptions'
    'dealButton';

  @media ${MediaQuery.SCREEN_MD_UP} {
    padding: 24px 0 0;
    grid-template-rows: 1fr 1fr;
    grid-template-areas:
      'actions dealButton'
      'store store'
      'title title'
      'priceAndOptions priceAndOptions';
  }
`;

export const ContentDealActionsLeft = styled.div`
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 0;
  justify-content: space-between;

  @media ${MediaQuery.SCREEN_MD_UP} {
    ${H2Heavy}
    justify-content: flex-start;
    margin-right: 8px;
  }
`;

export const StoreWrappingAndDate = styled.div`
  display: none;
  grid-area: store;
  align-items: center;
  justify-content: space-between;
  top: 16px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
  }
`;

export const TitleContainer = styled.div`
  grid-area: title;
  margin: 8px 0 8px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    margin: 8px 0;
  }
`;

export const Title = styled.h1`
  color: rgb(${({ theme }) => theme.colors.Alpha});
  ${H3Heavy}

  &[aria-disabled='true'] {
    color: rgb(${Colors.Gray});
  }

  @media ${MediaQuery.SCREEN_MD_UP} {
    margin-top: 12px;
  }
`;

export const DealStampStyled = styled(DealStamp)`
  grid-area: price;
  ${H1Heavy}

  & > span {
    ${H1Heavy}
  }

  & > small {
    ${SmallHeavy}
    margin-right: 4px;
  }

  @media ${MediaQuery.SCREEN_MD_UP} {
    ${Display}

    & > span {
      ${Display}
    }

    & > small {
      ${H1Heavy}
      margin-right: 8px;
    }
  }
`;

export const PriceWrappingAndOptionsDropdown = styled.div`
  grid-area: priceAndOptions;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > div:nth-child(2) {
    display: flex;
  }

  @media ${MediaQuery.SCREEN_MD_UP} {
    > div:nth-child(2) {
      display: none;
    }
  }
`;

export const StampAndCashbackPillWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const CashbackTagPillStyled = styled(CashbackTag)`
  margin-left: 8px;
  @media ${MediaQuery.SCREEN_MD_UP} {
    margin-left: 16px;
  }
`;

export const CashbackTagDoubleStyled = styled(CashbackTag)`
  position: absolute;
  z-index: 3;
  margin-top: 16px;
`;

export const ReadDealDescriptionLink = styled.div`
  grid-area: descriptionLink;
  display: none;
  ${CaptionHeavy}
  align-items: center;
  gap: 8px;

  i {
    font-size: 24px;
    color: rgb(${Theme.colors.Brand});
  }

  a {
    color: rgb(${Theme.colors.Alpha});
  }

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
    margin-top: 24px;
  }
`;

export const DealButtonContainer = styled.div`
  grid-area: dealButton;
  margin-top: 12px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    margin-top: 0;
  }
`;

export const DealButtonStyled = styled(DealActionStampButton)`
  max-height: unset;
  gap: 8px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    max-height: 40px;
    span {
      white-space: nowrap;
    }
  }
`;

export const DealVoteButtonStyled = styled(DealVoteButton)`
  max-height: 40px;
`;

export const DealOptionsDropdownContainer = styled.div`
  grid-area: options;
  display: none;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: flex;
  }
`;

export const DefaultTextStore = styled.div`
  grid-area: store;
  ${CaptionLight}
  color: rgb(${Colors.Gray});
`;

export const CommentsContent = styled.div`
  display: none;

  @media ${MediaQuery.SCREEN_MD_UP} {
    display: unset;
  }
`;

export const ImageContainerSkeleton = styled.div`
  margin: 8px 16px;
`;

export const ImageSkeleton = styled(DealImageSkeleton)`
  height: 303px;
  width: 100%;

  @media ${MediaQuery.SCREEN_MD_UP} {
    width: 512px;
  }
`;
export const ContainerSkeleton = styled.div`
  gap: 4px;
`;

const baseSkeleton = css`
  display: flex;
  flex-shrink: 0;
  margin-bottom: 4px;
  border-radius: 24px;
`;

export const ButtonSkeleton = styled(TextSkeleton)`
  ${baseSkeleton}
  height: 40px;
`;

export const DealTextSkeleton = styled(TextSkeleton)`
  ${baseSkeleton}
  height: 18px;
`;

export const DealTextSmallSkeleton = styled(TextSkeleton)`
  ${baseSkeleton}
  height: 12px;
`;

export const WhatsAppButtonSkeleton = styled(TextSkeleton)`
  ${baseSkeleton}
  height: 40px;
  width: 142px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    width: 40px;
  }
`;

export const DealButtonSkeleton = styled(TextSkeleton)`
  grid-area: dealButton;
  ${baseSkeleton}
  height: 40px;
  width: 100%;
  margin-top: 8px;

  @media ${MediaQuery.SCREEN_MD_UP} {
    width: 130px;
    margin-top: 0;
  }
`;

export const CustomTimestamp = styled(Timestamp)`
  display: none;
  &[data-mobile-visible='true'] {
    display: block;
  }
`;
